import { useMemo } from "react";
import { motion } from "framer-motion";
import { X, GitBranch, FolderGit2, Bot, Clock } from "lucide-react";
import type { ExecStatus, HiveExecution } from "@/types";
import { useExecution } from "@/store/executionStore";
import { useWorkflow } from "@/store/workflowStore";
import { EXEC_STATUS_META, RiskBadge, Pill } from "@/components/hive/shared";
import { Progress } from "@/components/ui/primitives";
import { SampleBadge } from "@/components/ui/SampleBadge";

const STATUS_FILTERS: { id: ExecStatus | "all"; label: string }[] = [
  { id: "all", label: "All" },
  { id: "running", label: "Running" },
  { id: "waiting", label: "Waiting" },
  { id: "needs-approval", label: "Approval" },
  { id: "failed", label: "Failed" },
  { id: "completed", label: "Completed" },
];

export function ExecutionList() {
  const executions = useExecution((s) => s.executions);
  const selectedId = useExecution((s) => s.selectedExecutionId);
  const select = useExecution((s) => s.selectExecution);
  const statusFilter = useExecution((s) => s.statusFilter);
  const setStatusFilter = useExecution((s) => s.setStatusFilter);
  const workflowFilter = useExecution((s) => s.workflowFilter);
  const setWorkflowFilter = useExecution((s) => s.setWorkflowFilter);
  const workflows = useWorkflow((s) => s.workflows);
  const wfName = workflows.find((w) => w.id === workflowFilter)?.name;

  const list = useMemo(
    () =>
      executions.filter(
        (e) => (statusFilter === "all" || e.status === statusFilter) && (!workflowFilter || e.workflowId === workflowFilter)
      ),
    [executions, statusFilter, workflowFilter]
  );

  return (
    <div className="flex h-full w-[336px] shrink-0 flex-col border-r border-app bg-surface/30">
      <div className="border-b border-app p-3">
        <div className="mb-2 flex items-center gap-2">
          <span className="text-sm font-semibold text-app">Executions</span>
          <span className="text-[11px] text-app-2 tabular-nums">{list.length}</span>
        </div>
        <div className="flex flex-wrap gap-1">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.id}
              onClick={() => setStatusFilter(f.id)}
              className={`rounded-full border px-2 py-0.5 text-[10px] font-medium transition ${
                statusFilter === f.id ? "border-cyan-500/50 bg-cyan-500/15 text-cyan-400" : "border-app text-app-2 hover:text-app-1"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        {workflowFilter && (
          <div className="mt-2 flex items-center gap-1.5 rounded-lg border border-cyan-500/30 bg-cyan-500/10 px-2.5 py-1 text-[10px] text-cyan-400">
            Filtered · {wfName}
            <button onClick={() => setWorkflowFilter(null)} className="ml-auto"><X size={11} /></button>
          </div>
        )}
      </div>

      <div className="flex-1 space-y-1.5 overflow-y-auto no-scrollbar p-2">
        {list.map((e) => (
          <ExecutionRow key={e.id} exec={e} active={e.id === selectedId} onClick={() => select(e.id)} />
        ))}
        {list.length === 0 && <div className="px-3 py-10 text-center text-xs text-app-2">No executions match.</div>}
      </div>
    </div>
  );
}

function ExecutionRow({ exec, active, onClick }: { exec: HiveExecution; active: boolean; onClick: () => void }) {
  const m = EXEC_STATUS_META[exec.status];
  const live = exec.status === "running";
  return (
    <button
      onClick={onClick}
      className={`w-full rounded-xl border p-2.5 text-left transition ${active ? "border-cyan-500/50 bg-cyan-500/[0.07]" : "border-app bg-surface/40 hover:border-app-strong"}`}
    >
      <div className="flex items-start justify-between gap-2">
        <span className="flex min-w-0 items-center gap-1.5">
          <span className="line-clamp-1 text-xs font-semibold leading-tight text-app">{exec.name}</span>
          {exec.isSample && <SampleBadge />}
        </span>
        <Pill color={m.color} dot={live} pulse={live}>{m.label}</Pill>
      </div>
      <div className="mt-1 truncate text-[10px] text-app-2">{exec.workflowName}</div>

      <div className="mt-2 flex items-center gap-1.5">
        {live ? (
          <motion.span className="text-[10px] font-medium text-cyan-400" animate={{ opacity: [0.55, 1, 0.55] }} transition={{ duration: 2, repeat: Infinity }}>
            ▸ {exec.currentStep}
          </motion.span>
        ) : (
          <span className="text-[10px] text-app-2">{exec.currentStep}</span>
        )}
        <span className="ml-auto text-[10px] tabular-nums text-app-2">{Math.round(exec.progress * 100)}%</span>
      </div>
      <Progress className="mt-1" value={exec.progress} color={m.color} />

      <div className="mt-2 flex items-center gap-2 text-[10px] text-app-2">
        {exec.repo && <span className="flex items-center gap-0.5"><FolderGit2 size={10} /> {exec.repo.split("/").pop()}</span>}
        {exec.agent && <span className="flex items-center gap-0.5"><Bot size={10} /> {exec.agent}</span>}
        <span className="flex items-center gap-0.5"><Clock size={10} /> {exec.duration}</span>
        <span className="ml-auto flex items-center gap-1.5">
          <span className="tabular-nums">${exec.cost.toFixed(2)}</span>
          <RiskBadge risk={exec.risk} />
        </span>
      </div>
    </button>
  );
}
